// Concierge queue — NEW console page (GAP-2, 18H §4.2). Bridge-read
// trust_center_leads via platform-trust-leads (this server is control-plane
// only, D-27 — never a direct table read). Leads with source chips
// (seeded/requested/direct), the right-party-captured indicator, SLA timer,
// claim-conflict routing (existing-owner decision: join / new profile /
// authorized sub-org — D-25/26, T1 concierge-manual per 18A §6.5), and the
// provision action -> POST /trust/provision (already built; this page is the
// missing read+act surface in front of it).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { RefreshCw, UserCheck, X, Sparkles, ShieldCheck, Clock3, Mail } from 'lucide-react';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { personaBase } from '@/lib/persona';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Kpi } from '@/components/shared/Kpi';
import { Pill } from '@/components/shared/badges';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/shared/ToastProvider';
import {
  getLeads, claimLead, dismissLead, provisionSaasOrg, leadSourceLabel, tierLabel,
  SAAS_LICENSE_TIERS, type TrustLead, type TrustLeadStatus, type SaasLicenseTier,
} from '@/lib/api/trust';

interface Organization { id: string; name: string; }

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface-raised)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const btnPri: React.CSSProperties = { ...btnSec, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5, verticalAlign: 'top' };

const STATUS_TABS: Array<{ key: TrustLeadStatus | ''; label: string }> = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'provisioned', label: 'Provisioned' },
  { key: 'dismissed', label: 'Dismissed' },
];

const SOURCE_OPTIONS = [
  { key: '', label: 'All sources' },
  { key: 'cta', label: 'Seeded' },
  { key: 'join_request', label: 'Requested' },
  { key: 'directory_invite', label: 'Direct' },
];

// D-45/18A §6.5 mapping: the lead's origin (`source`) is a different enum
// from `acquisition_source` recorded on the resulting saas license — this is
// the canonical translation between the two (also drives the chip label).
function acquisitionSourceForLead(source: string | null): 'share_cta' | 'requested' | 'direct' | 'concierge' {
  if (source === 'cta') return 'share_cta';
  if (source === 'join_request') return 'requested';
  if (source === 'directory_invite') return 'direct';
  return 'concierge';
}

function hasRightParty(lead: TrustLead): boolean {
  return !!(lead.contact_name && lead.contact_email);
}

// SLA timer — 24h first-touch target on an unclaimed ('new') lead. Once a
// lead has moved past 'new' the countdown framing no longer applies; show
// plain elapsed time instead.
function slaInfo(lead: TrustLead): { text: string; color: string } {
  const hours = (Date.now() - new Date(lead.created_at).getTime()) / 3_600_000;
  if (lead.status !== 'new') {
    return { text: formatRelativeTime(lead.created_at), color: 'var(--text-muted)' };
  }
  if (hours < 24) return { text: `${Math.max(0, Math.round(24 - hours))}h left`, color: '#16A34A' };
  if (hours < 72) return { text: `${Math.round(hours)}h — overdue`, color: '#D97706' };
  return { text: `${Math.round(hours)}h — SLA breached`, color: '#DC2626' };
}

type RoutingDecision = 'new' | 'join' | 'subOrg';

function ProvisionModal({
  lead, organizations, onClose, onProvisioned,
}: {
  lead: TrustLead;
  organizations: Organization[];
  onClose: () => void;
  onProvisioned: (leadId: string) => void;
}) {
  const { toast } = useToast();
  const [routing, setRouting] = useState<RoutingDecision>('new');
  const [orgId, setOrgId] = useState('');
  const [tier, setTier] = useState<SaasLicenseTier>('trust_base');
  const [tprm7OrgId, setTprm7OrgId] = useState('');
  const [newOrgName, setNewOrgName] = useState(lead.company || '');
  const [adminEmail, setAdminEmail] = useState(lead.contact_email || lead.email || '');
  const [adminName, setAdminName] = useState(lead.contact_name || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!orgId) { setError('The paying-account organization is required.'); return; }
    if (routing === 'join' && !tprm7OrgId.trim()) { setError('Existing TPRM7 org ID is required to join.'); return; }
    if (routing !== 'join' && (!newOrgName.trim() || !adminEmail.trim())) { setError('Hosted org name and admin email are required.'); return; }
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        organizationId: orgId,
        tier,
        trigger: 'concierge',
        acquisitionSource: acquisitionSourceForLead(lead.source),
        leadId: lead.id,
      };
      if (routing === 'join') {
        body.tprm7OrgId = tprm7OrgId.trim();
      } else {
        body.newOrg = { name: newOrgName.trim(), admin_email: adminEmail.trim(), admin_name: adminName.trim() || undefined };
      }
      if (lead.contact_email) {
        body.rightParty = { name: lead.contact_name || undefined, email: lead.contact_email, role: lead.contact_role || undefined };
      }
      await provisionSaasOrg(body);
      toast({ kind: 'success', title: 'Invite sent, vault seeded.' });
      onProvisioned(lead.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to provision');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent style={{ maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div>
          <DialogTitle style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Provision Trust Center</DialogTitle>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 16 }}>
            For {lead.company || lead.email} — source: {leadSourceLabel(lead.source)}
          </div>

          {hasRightParty(lead) && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'var(--ps-pill-green-bg)', border: '1px solid var(--ps-pill-green-tx)', marginBottom: 16, fontSize: 12.5 }}>
              <ShieldCheck size={15} color="var(--ps-pill-green-tx)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 600, color: 'var(--ps-pill-green-tx)' }}>Right party captured</div>
                <div style={{ color: 'var(--ps-pill-green-tx)' }}>{lead.contact_name} · {lead.contact_email}{lead.contact_role ? ` · ${lead.contact_role}` : ''}</div>
                <div style={{ color: 'var(--ps-pill-green-tx)', marginTop: 2 }}>The invite goes to this person, not the respondent ({lead.email}).</div>
              </div>
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Existing-owner routing decision (D-25/26)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {([
              ['new', 'No conflict — new profile', 'Default. Provisions a fresh hosted org and trust vault.'],
              ['join', 'Join existing owner', 'The lead’s domain is already claimed — route into the existing org instead of forking a second vault.'],
              ['subOrg', 'Authorized sub-org (Trust Pro)', 'A distinct entity on a shared parent domain, explicitly authorized by the verified parent owner.'],
            ] as Array<[RoutingDecision, string, string]>).map(([key, label, hint]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${routing === key ? 'var(--accent)' : 'var(--border)'}`, background: routing === key ? 'var(--accent-light)' : 'var(--surface)', cursor: 'pointer' }}>
                <input type="radio" name="routing" checked={routing === key} onChange={() => setRouting(key)} style={{ marginTop: 3, accentColor: 'var(--accent)' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{hint}</div>
                </div>
              </label>
            ))}
          </div>

          {routing === 'join' ? (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Existing TPRM7 org ID</label>
              <input value={tprm7OrgId} onChange={(e) => setTprm7OrgId(e.target.value)} placeholder="uuid" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box' }} />
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Hosted org name{routing === 'subOrg' ? ' (sub-org)' : ''}</label>
                <input value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Admin email</label>
                  <input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Admin name</label>
                  <input value={adminName} onChange={(e) => setAdminName(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box' }} />
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Paying-account org (license server)</label>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-primary)', background: 'var(--surface)' }}>
                <option value="">Select…</option>
                {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Tier</label>
              <select value={tier} onChange={(e) => setTier(e.target.value as SaasLicenseTier)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-primary)', background: 'var(--surface)' }}>
                {SAAS_LICENSE_TIERS.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
              </select>
            </div>
          </div>

          {error && <div style={{ fontSize: 12, color: 'var(--ps-pill-red-tx)', marginBottom: 10 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button onClick={onClose} style={btnSec}>Cancel</button>
            <button onClick={submit} disabled={submitting} style={{ ...btnPri, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Provisioning…' : 'Provision'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ConciergeQueuePage() {
  const location = useLocation();
  const base = personaBase(location.pathname);
  const { toast } = useToast();
  const [leads, setLeads] = useState<TrustLead[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<TrustLeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [dismissTarget, setDismissTarget] = useState<TrustLead | null>(null);
  const [provisionTarget, setProvisionTarget] = useState<TrustLead | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getLeads({ limit: 200 });
      setLeads(data.leads || []);
      setState('ready');
      // Secondary fetch (paying-account orgs for the provision modal) — a
      // failure here degrades that action, not the whole queue (partial).
      apiRequest<{ data: Organization[] }>('/organizations?limit=200')
        .then((resp) => setOrganizations(Array.isArray((resp as any)?.data) ? (resp as any).data : []))
        .catch(() => setOrganizations([]));
    } catch (err) {
      setLeads([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => leads.filter((l) => {
    if (statusTab && l.status !== statusTab) return false;
    if (sourceFilter && l.source !== sourceFilter) return false;
    return true;
  }), [leads, statusTab, sourceFilter]);

  const kpis = useMemo(() => {
    const total = leads.length;
    const active = leads.filter((l) => l.status === 'new' || l.status === 'contacted').length;
    const rightParty = leads.filter(hasRightParty).length;
    const provisioned = leads.filter((l) => l.status === 'provisioned').length;
    const overdue = leads.filter((l) => l.status === 'new' && (Date.now() - new Date(l.created_at).getTime()) / 3_600_000 >= 24).length;
    return { total, active, rightParty, provisioned, overdue };
  }, [leads]);

  const doClaim = async (lead: TrustLead) => {
    setRowBusy((p) => ({ ...p, [lead.id]: true }));
    setRowError((p) => { const e = { ...p }; delete e[lead.id]; return e; });
    try {
      const updated = await claimLead(lead.id);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? updated : l)));
      toast({ kind: 'success', title: 'Lead claimed — marked contacted.' });
    } catch (err) {
      setRowError((p) => ({ ...p, [lead.id]: err instanceof Error ? err.message : 'Failed to claim' }));
    } finally {
      setRowBusy((p) => ({ ...p, [lead.id]: false }));
    }
  };

  const doDismiss = async (lead: TrustLead) => {
    setRowBusy((p) => ({ ...p, [lead.id]: true }));
    try {
      const updated = await dismissLead(lead.id);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? updated : l)));
      toast({ kind: 'success', title: 'Lead dismissed.' });
    } catch (err) {
      setRowError((p) => ({ ...p, [lead.id]: err instanceof Error ? err.message : 'Failed to dismiss' }));
    } finally {
      setRowBusy((p) => ({ ...p, [lead.id]: false }));
    }
  };

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>{[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[160, 80, 140, 70, 80, 70, 120]} />)}</tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Concierge queue</h1>
          <p className="page-subtitle">Trust Center leads (bridge-read via platform-trust-leads, D-27 control-plane only) — CTA-seeded, join requests, and direct directory invites.</p>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={leads.length === 0}
        emptyIcon="inbox"
        emptyTitle="No leads yet"
        emptyBody="Nothing has landed in the concierge queue. Leads arrive from the assessment-completion CTA, join requests on claimed profiles, and direct directory invites."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Total leads" value={kpis.total} icon="inbox" />
          <Kpi label="Needs action" value={kpis.active} icon="clock" accent={kpis.overdue > 0 ? '#DC2626' : '#D97706'} hint={kpis.overdue > 0 ? `${kpis.overdue} SLA-overdue` : 'within SLA'} />
          <Kpi label="Right party captured" value={kpis.rightParty} icon="shield-check" accent="#059669" hint={kpis.total > 0 ? `${Math.round((kpis.rightParty / kpis.total) * 100)}% of leads` : undefined} />
          <Kpi label="Provisioned" value={kpis.provisioned} icon="check-circle" accent="#16A34A" />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key || 'all'}
              onClick={() => setStatusTab(t.key)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: `1px solid ${statusTab === t.key ? 'var(--accent)' : 'var(--border)'}`,
                background: statusTab === t.key ? 'var(--accent-light)' : 'var(--surface)', color: statusTab === t.key ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 12.5, fontWeight: statusTab === t.key ? 600 : 400, cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12.5, background: 'var(--surface)', marginLeft: 'auto' }}>
            {SOURCE_OPTIONS.map((s) => <option key={s.key || 'all'} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Lead</th>
                  <th style={th}>Source</th>
                  <th style={th}>Right party</th>
                  <th style={th}>Status</th>
                  <th style={th}><Clock3 size={11} style={{ verticalAlign: -1 }} /> SLA</th>
                  <th style={th}>Created</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No leads match this filter.</td></tr>
                ) : filtered.map((lead) => {
                  const sla = slaInfo(lead);
                  const busy = !!rowBusy[lead.id];
                  const actionable = lead.status !== 'provisioned' && lead.status !== 'dismissed';
                  return (
                    <tr key={lead.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      {/* ps-mask: rrweb maskTextClass (A20). Inbound leads —
                          people who left an address on a trust door and are not
                          customers of anything yet. The rest of the row (source,
                          status, SLA) is left legible: that is the queue
                          mechanics a bug report here is about, and it names
                          nobody. */}
                      <td style={td} className="ps-mask">
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{lead.company || lead.email}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={10} /> {lead.email}</div>
                      </td>
                      <td style={td}><Pill bg="var(--ps-pill-gray-bg)" fg="var(--ps-pill-gray-tx)">{leadSourceLabel(lead.source)}</Pill></td>
                      <td style={td}>
                        {hasRightParty(lead) ? (
                          <div title={`${lead.contact_name} · ${lead.contact_email}${lead.contact_role ? ` · ${lead.contact_role}` : ''}`}>
                            <Pill bg="var(--ps-pill-green-bg)" fg="var(--ps-pill-green-tx)"><ShieldCheck size={10} /> Captured</Pill>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, maxWidth: 180 }}>{lead.contact_name}</div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Not captured</span>
                        )}
                      </td>
                      <td style={td}><StatusBadge status={lead.status} /></td>
                      <td style={{ ...td, color: sla.color, fontWeight: sla.color !== 'var(--text-muted)' ? 600 : 400 }}>{sla.text}</td>
                      <td style={{ ...td, color: 'var(--text-muted)' }}>{formatRelativeTime(lead.created_at)}</td>
                      <td style={td}>
                        {actionable && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {lead.status === 'new' && (
                              <button onClick={() => doClaim(lead)} disabled={busy} style={{ ...btnSec, padding: '4px 8px', fontSize: 11.5 }}>
                                <UserCheck size={11} /> Claim
                              </button>
                            )}
                            <button onClick={() => setProvisionTarget(lead)} disabled={busy} style={{ ...btnPri, padding: '4px 8px', fontSize: 11.5 }}>
                              <Sparkles size={11} /> Provision
                            </button>
                            <button onClick={() => setDismissTarget(lead)} disabled={busy} style={{ ...btnSec, padding: '4px 8px', fontSize: 11.5 }}>
                              <X size={11} /> Dismiss
                            </button>
                          </div>
                        )}
                        {rowError[lead.id] && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>{rowError[lead.id]}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>

      <ConfirmDialog
        open={dismissTarget !== null}
        title="Dismiss lead?"
        body={dismissTarget ? `Dismiss the lead from ${dismissTarget.company || dismissTarget.email}? It drops out of the active queue.` : ''}
        confirmLabel="Dismiss"
        onConfirm={() => { const t = dismissTarget; setDismissTarget(null); if (t) doDismiss(t); }}
        onCancel={() => setDismissTarget(null)}
      />

      {provisionTarget && (
        <ProvisionModal
          lead={provisionTarget}
          organizations={organizations}
          onClose={() => setProvisionTarget(null)}
          onProvisioned={() => { setProvisionTarget(null); load(); }}
        />
      )}
    </div>
  );
}

export default ConciergeQueuePage;
